import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
} from "antd";
import { styled } from "styled-components";

import {
  useGetDistributorKycDetailsQuery,
  useSubmitDistributorKycMutation,
} from "../../../api/admin/distributors/distributorKycApi";
import { getKycError } from "./kycErrors";
import {
  emptyKycDetails,
  isDateField,
  kycFields,
  kycStatusLabels,
  requiresKycIdentity,
  validateKycRow,
  type KycActivationOptions,
  type KycDetails,
  type KycInventoryRow,
  type KycResult,
  type KycRowError,
  type KycStatus,
} from "./kyc";
import {
  downloadKycTemplate,
  parseKycCsv,
  parseKycExcel,
  type ParsedKycRow,
} from "./kycSpreadsheet";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Fields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 16px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const ActivationOptions = styled(Fields)`
  align-items: center;

  .ant-form-item-extra {
    font-size: 12px;
  }
`;
const Errors = styled.div`
  color: #b42318;
  white-space: pre-line;
`;
const receiptMessage =
  "Submitted confirms receipt of your request. It does not confirm activation.";
const choiceLabel = "Register under customer details.";
const kycGuidance =
  "No adds the entered KYC to the passport pool; " +
  "production pool passports require verification before use. " +
  "Yes uses this customer's KYC and sends their email to Gstarlink. " +
  "Gstarlink also records that KYC in its pool.";

export function KycStatusTag({ status }: { status?: KycStatus }) {
  const colors = {
    not_submitted: "default",
    processing: "blue",
    submitted: "green",
    rejected: "red",
    needs_review: "orange",
  };
  return (
    <Tag color={colors[status || "not_submitted"]}>
      {kycStatusLabels[status || "not_submitted"]}
    </Tag>
  );
}

export function InventoryKycDetails({
  distributorId,
  item,
  onClose,
  onSubmitted,
}: {
  distributorId: string;
  item: KycInventoryRow;
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}) {
  const {
    currentData: data,
    isFetching: loading,
    error,
    refetch: refresh,
  } = useGetDistributorKycDetailsQuery({ distributorId, iccid: item.iccid });
  const [submitKyc] = useSubmitDistributorKycMutation();
  const [edited, setEdited] = useState<Partial<KycDetails>>({});
  const [options, setOptions] = useState<KycActivationOptions>({});
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [validation, setValidation] = useState<string[]>([]);
  const [result, setResult] = useState<KycResult | null>(null);
  const details = { ...emptyKycDetails(), ...data?.details, ...edited };
  const useCustomerKyc =
    options.useCustomerKyc ?? data?.useCustomerKyc ?? false;
  const customerEmail = options.customerEmail ?? data?.customerEmail ?? "";
  const identityRequired = requiresKycIdentity({ ...details, useCustomerKyc });
  const summary = data || item;
  const shownStatus =
    result && summary.kycStatus === "not_submitted"
      ? result.kycStatus
      : summary.kycStatus;
  const locked =
    submitting ||
    loading ||
    !data ||
    !summary.canSubmitKyc ||
    (result !== null && summary.kycStatus !== "rejected");
  const legacySubmission =
    data &&
    !data.canSubmitKyc &&
    data.kycStatus !== "not_submitted" &&
    data.useCustomerKyc === undefined;
  useEffect(() => {
    if (data?.kycStatus !== "processing") {
      return;
    }
    const timer = setInterval(() => {
      void refresh();
    }, 5000);
    return () => clearInterval(timer);
  }, [data?.kycStatus, refresh]);

  async function submit() {
    const row = {
      iccid: item.iccid,
      ...details,
      useCustomerKyc,
      ...(useCustomerKyc ? { customerEmail: customerEmail.trim() } : {}),
    };
    const errors = validateKycRow(row, data || undefined);
    setValidation(errors);
    if (errors.length) {
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const response = await submitKyc({ distributorId, rows: [row] }).unwrap();
      setResult(response.rows[0]);
      await Promise.all([refresh(), onSubmitted()]);
    } catch (ex) {
      const failure = getKycError(ex);
      setFeedback(
        failure.errors.map((row) => row.message).join(" ") || failure.message,
      );
      await onSubmitted();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`ICCID ${item.iccid}`}
      open
      onCancel={onClose}
      width={720}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={submitting}
          disabled={locked}
          onClick={submit}
        >
          Submit KYC
        </Button>,
      ]}
    >
      <Stack>
        <Space wrap>
          <Typography.Text>
            Product: {item.simplyProductCode || "—"}
          </Typography.Text>
          <KycStatusTag status={shownStatus} />
        </Space>
        <Typography.Text type="secondary">{receiptMessage}</Typography.Text>
        {error && <Alert type="error" message={getKycError(error).message} />}
        {(feedback || result?.kycMessage || summary.kycMessage) && (
          <Alert
            type="warning"
            message={feedback || result?.kycMessage || summary.kycMessage}
          />
        )}
        {!summary.canSubmitKyc && summary.kycUnavailableReason && (
          <Alert type="info" message={summary.kycUnavailableReason} />
        )}
        {validation.length > 0 && (
          <Alert
            type="error"
            message="Check the details"
            description={validation.map((message) => (
              <div key={message}>{message}</div>
            ))}
          />
        )}
        <Form layout="vertical" disabled={locked}>
          {legacySubmission ? (
            <Typography.Paragraph type="secondary">
              KYC activation choice was not recorded for this submission.
            </Typography.Paragraph>
          ) : (
            <ActivationOptions>
              <Form.Item>
                <Checkbox
                  checked={useCustomerKyc}
                  onChange={(event) =>
                    setOptions((current) => ({
                      ...current,
                      useCustomerKyc: event.target.checked,
                    }))
                  }
                >
                  {choiceLabel}
                </Checkbox>
              </Form.Item>
              <Form.Item
                label="Email"
                required={useCustomerKyc}
                extra="Required when registering under customer details."
              >
                <Input
                  aria-label="Email"
                  aria-required={useCustomerKyc}
                  placeholder="Enter customer email address"
                  type="email"
                  maxLength={200}
                  disabled={locked || !useCustomerKyc}
                  value={customerEmail}
                  onChange={(event) =>
                    setOptions((current) => ({
                      ...current,
                      customerEmail: event.target.value,
                    }))
                  }
                />
              </Form.Item>
            </ActivationOptions>
          )}
          <Fields>
            {kycFields.map(([field, label]) => (
              <Form.Item
                key={field}
                label={label}
                required={
                  field === "arrivalDate" ||
                  (summary.passportRequired && field !== "middleName") ||
                  (!legacySubmission &&
                    identityRequired &&
                    field !== "middleName" &&
                    field !== "passportExpiryDate")
                }
              >
                <Input
                  aria-label={label}
                  type={isDateField(field) ? "date" : "text"}
                  value={details[field]}
                  maxLength={200}
                  onChange={(event) =>
                    setEdited((current) => ({
                      ...current,
                      [field]: event.target.value,
                    }))
                  }
                />
              </Form.Item>
            ))}
          </Fields>
        </Form>
      </Stack>
    </Modal>
  );
}

export function InventoryKycUpload({
  distributorId,
  inventory,
  onClose,
  onSubmitted,
}: {
  distributorId: string;
  inventory: KycInventoryRow[];
  onClose: () => void;
  onSubmitted: () => Promise<void>;
}) {
  const [submitKyc] = useSubmitDistributorKycMutation();
  const [rows, setRows] = useState<ParsedKycRow[]>([]);
  const [filename, setFilename] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverErrors, setServerErrors] = useState<KycRowError[]>([]);
  const [results, setResults] = useState<KycResult[]>([]);
  const inventoryById = useMemo(
    () => new Map(inventory.map((item) => [item.iccid, item])),
    [inventory],
  );
  const preview = rows.map((row) => ({
    ...row,
    errors: [
      ...row.errors,
      ...validateKycRow(row.details, inventoryById.get(row.details.iccid)),
      ...serverErrors
        .filter((error) => error.iccid === row.details.iccid)
        .map((error) => error.message),
    ],
  }));
  const hasErrors = preview.some((row) => row.errors.length > 0);

  async function readFile(file: File) {
    setBusy(true);
    setRows([]);
    setError(null);
    setResults([]);
    setServerErrors([]);
    setFilename(file.name);
    try {
      if (file.name.toLowerCase().endsWith(".csv")) {
        setRows(parseKycCsv(await file.text()));
      } else if (file.name.toLowerCase().endsWith(".xlsx")) {
        setRows(await parseKycExcel(await file.arrayBuffer()));
      } else {
        throw new Error("Choose an Excel (.xlsx) or CSV file.");
      }
    } catch (ex) {
      // Only template guidance is displayed, not document internals.
      const guidancePrefixes = [
        "Use the downloaded",
        "The file must",
        "The CSV could",
        "Choose an Excel",
        "The workbook has",
      ];
      const parserMessage = ex instanceof Error ? ex.message : "";
      setError(
        guidancePrefixes.some((prefix) => parserMessage.startsWith(prefix))
          ? parserMessage
          : "Unable to read the file. " +
              "Use the downloaded template and try again.",
      );
    } finally {
      setBusy(false);
    }
    return false;
  }

  async function submit() {
    if (hasErrors || !rows.length) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await submitKyc({
        distributorId,
        rows: rows.map((row) => row.details),
      }).unwrap();
      setResults(response.rows);
      await onSubmitted();
    } catch (ex) {
      const failure = getKycError(ex);
      setError(failure.message);
      setServerErrors(failure.errors);
    } finally {
      setBusy(false);
    }
  }

  const displayedResults = results.map((result) => {
    const current = inventoryById.get(result.iccid);
    return result.kycStatus === "processing" &&
      current &&
      current.kycStatus !== "not_submitted"
      ? current
      : result;
  });

  return (
    <Modal
      title="Upload KYC"
      open
      onCancel={onClose}
      width={1100}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={busy}
          disabled={busy || !rows.length || hasErrors || results.length > 0}
          onClick={submit}
        >
          Confirm and submit {rows.length || ""} rows
        </Button>,
      ]}
    >
      <Stack>
        <Typography.Paragraph>
          Use one row per ICCID. Enter dates as YYYY-MM-DD and keep ICCIDs and
          passport numbers as text. When using CSV in Excel, import those
          columns as Text. Maximum 200 rows. Correct every invalid row before
          submitting.
        </Typography.Paragraph>
        <Typography.Paragraph>
          Enter Yes or No in “Use customer KYC for activation”. Blank choices
          and old templates without that column default to No. Customer email is
          required for Yes.
        </Typography.Paragraph>
        <Typography.Paragraph type="secondary">
          {kycGuidance}
        </Typography.Paragraph>
        <Space wrap>
          <Button
            onClick={() =>
              downloadKycTemplate("xlsx").catch(() =>
                setError("Unable to download the template."),
              )
            }
          >
            Download Excel template
          </Button>
          <Button
            onClick={() =>
              downloadKycTemplate("csv").catch(() =>
                setError("Unable to download the template."),
              )
            }
          >
            Download CSV template
          </Button>
          <Upload
            accept=".xlsx,.csv"
            showUploadList={false}
            beforeUpload={readFile}
            disabled={busy}
          >
            <Button loading={busy}>Choose file</Button>
          </Upload>
          <Typography.Text>{filename}</Typography.Text>
        </Space>
        {error && <Alert type="error" message={error} />}
        {results.length === 0 && rows.length > 0 && (
          <>
            <Alert
              type={hasErrors ? "error" : "success"}
              message={
                hasErrors
                  ? "Fix all rows before submitting. " +
                    "Nothing has been submitted."
                  : `${rows.length} rows ready. Confirm to submit.`
              }
            />
            <Table
              dataSource={preview}
              rowKey="rowNumber"
              size="small"
              scroll={{ x: 1600 }}
              pagination={{ pageSize: 10 }}
              columns={[
                { title: "Row", dataIndex: "rowNumber", width: 60 },
                {
                  title: "ICCID",
                  render: (_, row) => row.details.iccid,
                  width: 200,
                },
                ...kycFields.map(([field, label]) => ({
                  title: label,
                  render: (_: unknown, row: (typeof preview)[number]) =>
                    row.details[field],
                  width: 140,
                })),
                {
                  title: "Use customer KYC for activation",
                  render: (_, row) =>
                    row.details.useCustomerKyc ? "Yes" : "No",
                  width: 180,
                },
                {
                  title: "Customer email",
                  render: (_, row) => row.details.customerEmail || "—",
                  width: 220,
                },
                {
                  title: "Validation",
                  render: (_, row) =>
                    row.errors.length ? (
                      <Errors>{row.errors.join("\n")}</Errors>
                    ) : (
                      "Ready"
                    ),
                  width: 300,
                },
              ]}
            />
          </>
        )}
        {results.length > 0 && (
          <>
            <Alert type="info" message={receiptMessage} />
            <Table
              dataSource={displayedResults}
              rowKey="iccid"
              size="small"
              columns={[
                { title: "ICCID", dataIndex: "iccid" },
                {
                  title: "Submission status",
                  render: (_, row) => <KycStatusTag status={row.kycStatus} />,
                },
                { title: "Details", dataIndex: "kycMessage" },
              ]}
            />
          </>
        )}
      </Stack>
    </Modal>
  );
}
